<template>
    <div
        class="relative w-full"
        :class="{ 'opacity-60 pointer-events-none': disabled }"
    >
        <label v-if="label" :class="labelClass" class="block mb-1">{{
            label
        }}</label>

        <label
            v-if="floatingLabel"
            :class="[
                'absolute z-10 text-[11px] leading-none transition-all duration-200 px-1 rounded',
                iconFront ? 'left-[42px]' : 'left-3',
                'top-1',
                displayedLabel !== placeholder && displayedLabel !== 'Not Found'
                    ? 'text-blue-600'
                    : 'text-gray-500',
            ]"
            aria-hidden="true"
        >
            {{ floatingLabel }}
        </label>

        <div
            :class="[
                'h-10 min-h-[40px] flex items-center gap-2 rounded-lg border bg-white text-sm',
                'px-3 cursor-pointer select-none transition ring-0 outline-none',
                isOpen ? 'border-blue-500' : borderClass,
                disabled ? 'bg-gray-100' : 'bg-white',
                cssClass,
            ]"
            role="combobox"
            :aria-expanded="isOpen.toString()"
            :aria-controls="listId"
            :tabindex="disabled ? -1 : 0"
            @click="toggleDropdown"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.enter.prevent="onEnter"
            @keydown.esc.prevent="close"
        >
            <span v-if="iconFront" class="shrink-0">
                <AppIcon
                    :icon="iconFront"
                    class="text-gray-600"
                    width="18"
                    height="18"
                />
            </span>

            <span :class="displayedLabelClass" class="truncate">
                {{ displayedLabel }}
            </span>

            <span class="ml-auto shrink-0">
                <AppIcon
                    icon="mdi:chevron-down"
                    class="w-4 h-4 text-gray-600"
                />
            </span>
        </div>

        <div
            v-if="isOpen"
            class="absolute z-[9999] mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.enter.prevent="onEnter"
            @keydown.esc.prevent="close"
        >
            <div class="p-2 border-b border-gray-200">
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="Search..."
                    class="w-full px-3 text-sm border rounded-md h-9 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    :aria-controls="listId"
                    ref="searchRef"
                />
            </div>

            <ul :id="listId" role="listbox" class="py-1 overflow-auto max-h-48">
                <li
                    v-for="(option, idx) in filteredOptions"
                    :key="option.value"
                    role="option"
                    :aria-selected="option.value === modelValue"
                    @click="selectOption(option)"
                    @mouseenter="focusedIndex = idx"
                    :class="[
                        'px-3 py-2 text-sm cursor-pointer flex items-center gap-2',
                        idx === focusedIndex ? 'bg-blue-50' : 'bg-white',
                        option.value === modelValue
                            ? 'font-medium text-gray-900'
                            : 'text-gray-700',
                    ]"
                >
                    {{ option.label }}
                </li>

                <li
                    v-if="filteredOptions.length === 0"
                    class="px-3 py-2 text-sm text-gray-500"
                >
                    Not Found
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: "BaseSelectCompact",
    props: {
        label: { type: String, default: null },
        floatingLabel: { type: String, default: "" },
        labelClass: {
            type: String,
            default: "text-xs text-gray-700 font-medium",
        },
        options: { type: Array, required: true, default: () => [] },
        placeholder: { type: String, default: "Select option" },
        iconFront: { type: String, default: "" },
        modelValue: { type: [String, Number, null], default: null },
        cssClass: { type: String, default: "" },
        disabled: { type: Boolean, default: false },
    },

    data() {
        return {
            isOpen: false,
            displayedLabel: this.placeholder,
            searchQuery: "",
            focusedIndex: -1,
            borderClass: "border-gray-300",
            listId: `bsclist-${Math.random().toString(36).slice(2, 9)}`,
        };
    },

    computed: {
        selectedOption() {
            return this.options.find((o) => o.value === this.modelValue);
        },
        displayedLabelClass() {
            return !this.displayedLabel ||
                this.displayedLabel === this.placeholder ||
                this.displayedLabel === "Not Found"
                ? "text-gray-500"
                : "text-gray-800";
        },
        filteredOptions() {
            if (!this.searchQuery) return this.options;
            const q = this.searchQuery.toLowerCase();
            return this.options.filter((o) =>
                String(o.label).toLowerCase().includes(q)
            );
        },
    },

    watch: {
        modelValue(newVal) {
            const sel = this.options.find((o) => o.value === newVal);
            this.displayedLabel = sel ? sel.label : this.placeholder;
        },
        isOpen(open) {
            if (open) {
                this.$nextTick(() => {
                    this.focusedIndex = Math.max(
                        0,
                        this.filteredOptions.findIndex(
                            (o) => o.value === this.modelValue
                        )
                    );
                    this.$refs.searchRef?.focus();
                });
            } else {
                this.searchQuery = "";
                this.focusedIndex = -1;
            }
        },
    },

    mounted() {
        if (this.modelValue) {
            const sel = this.options.find((o) => o.value === this.modelValue);
            this.displayedLabel = sel ? sel.label : this.placeholder;
        }
        document.addEventListener("click", this.onDocumentClick);
    },
    unmounted() {
        document.removeEventListener("click", this.onDocumentClick);
    },

    methods: {
        // dipertahankan untuk kompatibilitas, tapi tanpa argumen agar lolos ESLint
        handleDropdownClick() {},

        toggleDropdown() {
            if (this.disabled) return;
            this.isOpen = !this.isOpen;
        },
        close() {
            this.isOpen = false;
        },

        selectOption(option) {
            this.$emit("update:modelValue", option.value);
            this.displayedLabel = option.label;
            this.close();
        },

        onDocumentClick(ev) {
            if (!this.$el.contains(ev.target)) this.close();
        },

        onArrowDown() {
            if (!this.isOpen) this.isOpen = true;
            if (this.filteredOptions.length === 0) return;
            this.focusedIndex =
                (this.focusedIndex + 1) % this.filteredOptions.length;
            this.scrollFocusedIntoView();
        },
        onArrowUp() {
            if (!this.isOpen) this.isOpen = true;
            if (this.filteredOptions.length === 0) return;
            this.focusedIndex =
                (this.focusedIndex - 1 + this.filteredOptions.length) %
                this.filteredOptions.length;
            this.scrollFocusedIntoView();
        },
        onEnter() {
            if (!this.isOpen) {
                this.isOpen = true;
                return;
            }
            const opt = this.filteredOptions[this.focusedIndex];
            if (opt) this.selectOption(opt);
        },
        scrollFocusedIntoView() {
            this.$nextTick(() => {
                const list = this.$el.querySelector(`#${this.listId}`);
                const item = list?.children?.[this.focusedIndex];
                if (list && item) {
                    const itemTop = item.offsetTop;
                    const itemBottom = itemTop + item.offsetHeight;
                    if (itemTop < list.scrollTop) list.scrollTop = itemTop;
                    else if (itemBottom > list.scrollTop + list.clientHeight)
                        list.scrollTop = itemBottom - list.clientHeight;
                }
            });
        },
    },
};
</script>

<style scoped>
:deep(input)::placeholder {
    color: #9ca3af;
}
</style>
